// The Rules page's form builder: the common rule shapes as pickers over the
// active database's ontology, written out as QQL by lib/ruleBuilder. The page
// owns saving and testing; this component owns the form.
import { useMemo, useState } from 'react';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { EntityPicker } from '@/components/EntityPicker';
import { Button } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';
import { useInstancesOf } from '@/lib/useDataset';
import { localName, type OntologySchema, type SchemaProperty } from '@/lib/useOntology';
import {
  opWords,
  type BuilderRule,
  type CompareOp,
  type Condition,
  type LiteralKind,
  type Outcome,
  type Test,
} from '@/lib/ruleBuilder';
import { cn } from '@/lib/utils';

export type ValueKind = 'thing' | 'number' | 'date' | 'text' | 'boolean';

const NUMBER_TYPES = new Set([
  'integer', 'int', 'long', 'short', 'byte', 'decimal', 'double', 'float',
  'nonNegativeInteger', 'positiveInteger', 'negativeInteger', 'nonPositiveInteger',
  'unsignedInt', 'unsignedLong', 'unsignedShort', 'unsignedByte',
]);
const DATE_TYPES = new Set(['date', 'dateTime', 'dateTimeStamp']);

function rangeLocal(range: string): string {
  const idx = Math.max(range.lastIndexOf('#'), range.lastIndexOf(':'), range.lastIndexOf('/'));
  return range.slice(idx + 1);
}

/** What kind of value a property holds, from its declaration; a property the
 *  ontology does not declare reads from the test it already carries. */
function valueKindOf(property: SchemaProperty | undefined, test?: Test): ValueKind {
  if (property) {
    if (property.kind === 'object') return 'thing';
    const local = property.range[0] ? rangeLocal(property.range[0]) : '';
    if (NUMBER_TYPES.has(local)) return 'number';
    if (DATE_TYPES.has(local)) return 'date';
    if (local === 'boolean') return 'boolean';
    return 'text';
  }
  switch (test?.kind) {
    case 'is':
      return 'thing';
    case 'flag':
      return 'boolean';
    case 'anyOf':
      return test.valueKind;
    case 'compare':
      return test.valueKind;
    case 'contains':
      return 'text';
    default:
      return 'number';
  }
}

function defaultTest(kind: ValueKind): Test {
  switch (kind) {
    case 'thing':
      return { kind: 'is', term: '' };
    case 'boolean':
      return { kind: 'flag', value: true };
    case 'text':
      return { kind: 'compare', op: 'eq', valueKind: 'text', value: '' };
    default:
      return { kind: 'compare', op: 'gt', valueKind: kind, value: '' };
  }
}

type OpKey = 'is' | 'anyOf' | 'exists' | 'true' | 'false' | 'contains' | CompareOp;

function opsFor(kind: ValueKind): Array<[OpKey, string]> {
  const compare = (k: LiteralKind, ops: CompareOp[]) => ops.map((op): [OpKey, string] => [op, opWords(k, op)]);
  switch (kind) {
    case 'thing':
      return [['is', 'is'], ['anyOf', 'is any of'], ['exists', 'has any value']];
    case 'boolean':
      return [['true', 'is true'], ['false', 'is false'], ['exists', 'has any value']];
    case 'number':
      return [...compare('number', ['eq', 'ne', 'gt', 'ge', 'lt', 'le']), ['anyOf', 'is any of'], ['exists', 'has any value']];
    case 'date':
      return [...compare('date', ['eq', 'ne', 'gt', 'ge', 'lt', 'le']), ['exists', 'has any value']];
    case 'text':
      return [['eq', 'is'], ['ne', 'is not'], ['contains', 'contains'], ['anyOf', 'is any of'], ['exists', 'has any value']];
  }
}

function opOf(test: Test): OpKey {
  switch (test.kind) {
    case 'is':
      return 'is';
    case 'anyOf':
      return 'anyOf';
    case 'exists':
      return 'exists';
    case 'flag':
      return test.value ? 'true' : 'false';
    case 'contains':
      return 'contains';
    case 'compare':
    case 'compareProperty':
      return test.op;
  }
}

function testFor(op: OpKey, kind: ValueKind, prev: Test): Test {
  switch (op) {
    case 'is':
      return { kind: 'is', term: prev.kind === 'is' ? prev.term : '' };
    case 'anyOf':
      return { kind: 'anyOf', valueKind: kind === 'thing' ? 'thing' : kind === 'number' ? 'number' : 'text', values: [] };
    case 'exists':
      return { kind: 'exists' };
    case 'true':
    case 'false':
      return { kind: 'flag', value: op === 'true' };
    case 'contains':
      return { kind: 'contains', text: prev.kind === 'contains' ? prev.text : '' };
    default:
      if (prev.kind === 'compareProperty') return { ...prev, op };
      return {
        kind: 'compare',
        op,
        valueKind: kind === 'number' || kind === 'date' ? kind : 'text',
        value: prev.kind === 'compare' ? prev.value : '',
      };
  }
}

// ---- pickers ----

function byLabel<T extends { uri: string; label: string | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.label ?? localName(a.uri)).localeCompare(b.label ?? localName(b.uri)));
}

function ClassSelect({
  value,
  onChange,
  schema,
  label,
  emptyText,
}: {
  value: string;
  onChange: (uri: string) => void;
  schema: OntologySchema;
  label: string;
  emptyText: string;
}) {
  const classes = byLabel(schema.classes);
  const known = !value || classes.some((c) => c.uri === value);
  return (
    <select aria-label={label} className={fieldClass('normal', 'min-w-0 py-1')} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{emptyText}</option>
      {!known && <option value={value}>{localName(value)}</option>}
      {classes.map((c) => (
        <option key={c.uri} value={c.uri}>
          {c.label ?? localName(c.uri)}
        </option>
      ))}
    </select>
  );
}

/** The class and every class above it, so a property declared on a parent is
 *  offered on its subclasses. */
function lineage(schema: OntologySchema, cls: string | null): Set<string> {
  const out = new Set<string>();
  const parents = new Map(schema.classes.map((c) => [c.uri, c.super_classes]));
  const walk = (uri: string) => {
    if (out.has(uri)) return;
    out.add(uri);
    for (const p of parents.get(uri) ?? []) walk(p);
  };
  if (cls) walk(cls);
  return out;
}

function PropertySelect({
  value,
  onChange,
  schema,
  forClass,
  only,
  label,
}: {
  value: string;
  onChange: (uri: string) => void;
  schema: OntologySchema;
  forClass: string | null;
  only?: 'object' | 'datatype';
  label: string;
}) {
  const classes = lineage(schema, forClass);
  const props = byLabel(schema.properties.filter((p) => !only || p.kind === only));
  const own = props.filter((p) => p.domain.some((d) => classes.has(d)));
  const rest = props.filter((p) => !own.includes(p));
  const known = !value || props.some((p) => p.uri === value);
  const option = (p: SchemaProperty) => (
    <option key={p.uri} value={p.uri}>
      {p.label ?? localName(p.uri)}
    </option>
  );
  const forName = forClass ? (schema.classes.find((c) => c.uri === forClass)?.label ?? localName(forClass)) : '';
  return (
    <select aria-label={label} className={fieldClass('normal', 'min-w-0 py-1')} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Pick a property…</option>
      {!known && <option value={value}>{localName(value)}</option>}
      {own.length > 0 && <optgroup label={`Declared for ${forName}`}>{own.map(option)}</optgroup>}
      {rest.length > 0 && <optgroup label={own.length > 0 ? 'Other properties' : 'Properties'}>{rest.map(option)}</optgroup>}
    </select>
  );
}

/** Pick one thing: from the list of things of the property's range when the
 *  list is short enough to read, otherwise by search. */
function ThingInput({
  range,
  value,
  onChange,
  name,
  label,
}: {
  range: string | null;
  value: string;
  onChange: (uri: string) => void;
  name: (term: string) => string;
  label: string;
}) {
  const instances = useInstancesOf(range, [ALL_GRAPHS]);
  const rows = instances.data ?? [];
  if (range && rows.length > 0 && rows.length < 200) {
    const known = !value || rows.some((r) => r.uri === value);
    return (
      <select aria-label={label} className={fieldClass('normal', 'min-w-0 py-1')} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Pick one…</option>
        {!known && <option value={value}>{name(value)}</option>}
        {[...rows]
          .sort((a, b) => (a.label || localName(a.uri)).localeCompare(b.label || localName(b.uri)))
          .map((r) => (
            <option key={r.uri} value={r.uri}>
              {r.label || localName(r.uri)}
            </option>
          ))}
      </select>
    );
  }
  return (
    <EntityPicker
      selected={value ? { uri: value, label: name(value) } : null}
      onSelect={(sel) => onChange(sel?.uri ?? '')}
      placeholder="Search for it…"
      label={label}
    />
  );
}

function ValueList({
  values,
  onChange,
  render,
  adder,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  render: (v: string) => string;
  adder: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {values.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-md border border-input bg-muted px-2 py-0.5 text-sm">
          {render(v)}
          <Button variant="ghost" size="icon" aria-label={`Remove ${render(v)}`} onClick={() => onChange(values.filter((x) => x !== v))}>
            ×
          </Button>
        </span>
      ))}
      {adder}
    </div>
  );
}

function TypedAdder({ kind, onAdd }: { kind: 'number' | 'text'; onAdd: (v: string) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    if (text.trim()) onAdd(kind === 'number' ? text.trim() : text);
    setText('');
  };
  return (
    <span className="inline-flex items-center gap-1">
      <input
        aria-label="Value to add"
        type={kind === 'number' ? 'number' : 'text'}
        className={fieldClass('normal', 'w-28 py-1')}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
      />
      <Button variant="secondary" size="sm" onClick={add}>
        Add
      </Button>
    </span>
  );
}

// ---- a condition ----

function ConditionRow({
  condition,
  onChange,
  onRemove,
  rule,
  schema,
  name,
  removeLabel,
}: {
  condition: Condition;
  onChange: (c: Condition) => void;
  onRemove: () => void;
  rule: BuilderRule;
  schema: OntologySchema;
  name: (term: string) => string;
  removeLabel: string;
}) {
  const owner = condition.on === 'linked' && rule.link ? rule.link.cls : rule.subjectClass;
  const property = schema.properties.find((p) => p.uri === condition.property);
  const kind = valueKindOf(property, condition.test);
  const range = property?.kind === 'object' ? (property.range[0] ?? null) : null;
  const t = condition.test;
  const set = (test: Test) => onChange({ ...condition, test });
  const linkedName = rule.link ? (rule.link.cls ? name(rule.link.cls) : name(rule.link.property)) : '';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
      <Button variant="danger" size="sm" onClick={onRemove}>
        {removeLabel}
      </Button>
      {rule.link ? (
        <select
          aria-label="Whose property"
          className={fieldClass('normal', 'py-1')}
          value={condition.on}
          onChange={(e) => onChange({ ...condition, on: e.target.value === 'linked' ? 'linked' : 'subject' })}
        >
          <option value="subject">its</option>
          <option value="linked">that {linkedName}'s</option>
        </select>
      ) : (
        <span className="text-sm text-tres-text-secondary">its</span>
      )}
      <PropertySelect
        label="Property"
        value={condition.property}
        schema={schema}
        forClass={owner}
        onChange={(uri) => {
          const p = schema.properties.find((x) => x.uri === uri);
          onChange({ ...condition, property: uri, test: defaultTest(valueKindOf(p)) });
        }}
      />
      {condition.property && (
        <select
          aria-label="How it compares"
          className={fieldClass('normal', 'py-1')}
          value={opOf(t)}
          onChange={(e) => set(testFor(e.target.value as OpKey, kind, t))}
        >
          {opsFor(kind).map(([key, words]) => (
            <option key={key} value={key}>
              {words}
            </option>
          ))}
        </select>
      )}
      {(t.kind === 'compare' || t.kind === 'compareProperty') && (kind === 'number' || kind === 'date') && (
        <select
          aria-label="Compare with"
          className={fieldClass('normal', 'py-1')}
          value={t.kind === 'compare' ? 'value' : 'property'}
          onChange={(e) =>
            set(
              e.target.value === 'property'
                ? { kind: 'compareProperty', op: t.op, property: '' }
                : { kind: 'compare', op: t.op, valueKind: kind, value: '' },
            )
          }
        >
          <option value="value">a value</option>
          <option value="property">another of its properties</option>
        </select>
      )}
      {t.kind === 'is' && (
        <ThingInput label="Value" range={range} value={t.term} name={name} onChange={(term) => set({ ...t, term })} />
      )}
      {t.kind === 'compare' && (
        <input
          aria-label="Value"
          type={t.valueKind === 'number' ? 'number' : t.valueKind === 'date' ? 'date' : 'text'}
          className={fieldClass('normal', 'w-40 py-1')}
          value={t.value}
          onChange={(e) => set({ ...t, value: e.target.value })}
        />
      )}
      {t.kind === 'compareProperty' && (
        <PropertySelect
          label="Other property"
          value={t.property}
          schema={schema}
          forClass={owner}
          only="datatype"
          onChange={(uri) => set({ ...t, property: uri })}
        />
      )}
      {t.kind === 'contains' && (
        <input
          aria-label="Text to look for"
          type="text"
          className={fieldClass('normal', 'w-40 py-1')}
          value={t.text}
          onChange={(e) => set({ ...t, text: e.target.value })}
        />
      )}
      {t.kind === 'anyOf' && (
        <ValueList
          values={t.values}
          onChange={(values) => set({ ...t, values })}
          render={(v) => (t.valueKind === 'thing' ? name(v) : v)}
          adder={
            t.valueKind === 'thing' ? (
              <ThingInput
                label="Value to add"
                range={range}
                value=""
                name={name}
                onChange={(term) => term && !t.values.includes(term) && set({ ...t, values: [...t.values, term] })}
              />
            ) : (
              <TypedAdder kind={t.valueKind} onAdd={(v) => !t.values.includes(v) && set({ ...t, values: [...t.values, v] })} />
            )
          }
        />
      )}
    </div>
  );
}

// ---- the outcome ----

function OutcomeEditor({
  outcome,
  onChange,
  rule,
  schema,
  name,
}: {
  outcome: Outcome;
  onChange: (o: Outcome) => void;
  rule: BuilderRule;
  schema: OntologySchema;
  name: (term: string) => string;
}) {
  const property = 'property' in outcome ? schema.properties.find((p) => p.uri === outcome.property) : undefined;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="What follows"
        className={fieldClass('normal', 'py-1')}
        value={outcome.kind}
        onChange={(e) => {
          const kind = e.target.value;
          onChange(
            kind === 'type'
              ? { kind: 'type', cls: '' }
              : kind === 'link'
                ? { kind: 'link', property: '', term: '' }
                : kind === 'value'
                  ? { kind: 'value', property: '', valueKind: 'text', value: '' }
                  : { kind: 'select' },
          );
        }}
      >
        <option value="type">it is a…</option>
        <option value="link">it gets a link or a finding…</option>
        <option value="value">it gets a value…</option>
        <option value="select">nothing is added: the rule only picks them out</option>
      </select>
      {outcome.kind === 'type' && (
        <ClassSelect
          label="Kind of thing it becomes"
          value={outcome.cls}
          schema={schema}
          emptyText="Pick a kind of thing…"
          onChange={(cls) => onChange({ ...outcome, cls })}
        />
      )}
      {outcome.kind === 'link' && (
        <>
          <PropertySelect
            label="Property it gets"
            value={outcome.property}
            schema={schema}
            forClass={rule.subjectClass}
            only="object"
            onChange={(uri) => onChange({ ...outcome, property: uri, term: '' })}
          />
          {outcome.property && (
            <ThingInput
              label="What it gets"
              range={property?.range[0] ?? null}
              value={outcome.term}
              name={name}
              onChange={(term) => onChange({ ...outcome, term })}
            />
          )}
        </>
      )}
      {outcome.kind === 'value' && (
        <>
          <PropertySelect
            label="Property it gets"
            value={outcome.property}
            schema={schema}
            forClass={rule.subjectClass}
            only="datatype"
            onChange={(uri) => {
              const kind = valueKindOf(schema.properties.find((p) => p.uri === uri));
              onChange({
                kind: 'value',
                property: uri,
                valueKind: kind === 'number' || kind === 'boolean' ? kind : 'text',
                value: kind === 'boolean' ? 'true' : '',
              });
            }}
          />
          {outcome.property &&
            (outcome.valueKind === 'boolean' ? (
              <select
                aria-label="Value it gets"
                className={fieldClass('normal', 'py-1')}
                value={outcome.value}
                onChange={(e) => onChange({ ...outcome, value: e.target.value })}
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <input
                aria-label="Value it gets"
                type={outcome.valueKind === 'number' ? 'number' : 'text'}
                className={fieldClass('normal', 'w-40 py-1')}
                value={outcome.value}
                onChange={(e) => onChange({ ...outcome, value: e.target.value })}
              />
            ))}
        </>
      )}
    </div>
  );
}

// ---- the form ----

function Section({ title, help, children }: { title: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-sm font-medium text-tres-text-primary">{title}</h3>
        {help && <p className="text-xs text-tres-text-secondary">{help}</p>}
      </div>
      {children}
    </div>
  );
}

export function RuleBuilder({
  rule,
  onChange,
  schema,
  name,
}: {
  rule: BuilderRule;
  onChange: (rule: BuilderRule) => void;
  schema: OntologySchema;
  name: (term: string) => string;
}) {
  const blankCondition = useMemo<Condition>(() => ({ on: 'subject', property: '', test: { kind: 'exists' } }), []);
  const linkProperty = rule.link ? schema.properties.find((p) => p.uri === rule.link?.property) : undefined;
  const patchCondition = (list: 'conditions' | 'unless', i: number, c: Condition | null) =>
    onChange({
      ...rule,
      [list]: c ? rule[list].map((x, j) => (j === i ? c : x)) : rule[list].filter((_, j) => j !== i),
    });
  return (
    <div className="space-y-4">
      <Section title="The rule is about">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-tres-text-secondary">every</span>
          <ClassSelect
            label="Kind of thing the rule is about"
            value={rule.subjectClass}
            schema={schema}
            emptyText="Pick a kind of thing…"
            onChange={(cls) => onChange({ ...rule, subjectClass: cls })}
          />
        </div>
        {rule.link ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
            <Button variant="danger" size="sm" onClick={() => onChange({ ...rule, link: null })}>
              Remove link
            </Button>
            <span className="text-sm text-tres-text-secondary">that has a</span>
            <PropertySelect
              label="Link to the other thing"
              value={rule.link.property}
              schema={schema}
              forClass={rule.subjectClass}
              only="object"
              onChange={(uri) => {
                const range = schema.properties.find((p) => p.uri === uri)?.range[0] ?? null;
                const cls = range && schema.classes.some((c) => c.uri === range) ? range : null;
                onChange({ ...rule, link: { property: uri, cls } });
              }}
            />
            <span className="text-sm text-tres-text-secondary">that is a</span>
            <ClassSelect
              label="Kind of the linked thing"
              value={rule.link.cls ?? ''}
              schema={schema}
              emptyText={linkProperty ? 'any kind of thing' : 'Pick a kind of thing…'}
              onChange={(cls) => onChange({ ...rule, link: rule.link ? { ...rule.link, cls: cls || null } : null })}
            />
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={!rule.subjectClass}
            onClick={() => onChange({ ...rule, link: { property: '', cls: null } })}
          >
            Follow a link to another thing
          </Button>
        )}
      </Section>

      <Section
        title="When"
        help="Every condition must hold. Leave this empty for a rule about every one of them."
      >
        {rule.conditions.map((c, i) => (
          <ConditionRow
            key={i}
            condition={c}
            rule={rule}
            schema={schema}
            name={name}
            removeLabel="Remove"
            onChange={(next) => patchCondition('conditions', i, next)}
            onRemove={() => patchCondition('conditions', i, null)}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          disabled={!rule.subjectClass}
          onClick={() => onChange({ ...rule, conditions: [...rule.conditions, blankCondition] })}
        >
          Add a condition
        </Button>
      </Section>

      <Section title="Unless" help="The rule does not hold for a thing that meets any of these.">
        {rule.unless.map((c, i) => (
          <ConditionRow
            key={i}
            condition={c}
            rule={rule}
            schema={schema}
            name={name}
            removeLabel="Remove"
            onChange={(next) => patchCondition('unless', i, next)}
            onRemove={() => patchCondition('unless', i, null)}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          disabled={!rule.subjectClass}
          onClick={() => onChange({ ...rule, unless: [...rule.unless, blankCondition] })}
        >
          Add an exception
        </Button>
      </Section>

      <Section title="Then">
        <OutcomeEditor outcome={rule.outcome} onChange={(outcome) => onChange({ ...rule, outcome })} rule={rule} schema={schema} name={name} />
      </Section>
    </div>
  );
}

/** The two readings of one rule, side by side: the sentence and the QQL. */
export function RuleReadings({ sentence, when, then, problems }: { sentence: string; when: string; then: string; problems: string[] }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-md bg-muted/50 px-3 py-2">
        <h3 className="text-xs font-medium text-tres-text-secondary">Reads as</h3>
        <p className="mt-1 text-sm text-tres-text-primary" data-testid="rule-sentence">
          {sentence}
        </p>
      </div>
      <div className="rounded-md bg-muted/50 px-3 py-2">
        <h3 className="text-xs font-medium text-tres-text-secondary">QQL it saves</h3>
        {problems.length === 0 ? (
          <pre className={cn('mt-1 whitespace-pre-wrap font-mono text-xs text-tres-text-primary')} data-testid="rule-qql">
            {`WHEN ${when}`}
            {then ? `\nTHEN ${then}` : ''}
          </pre>
        ) : (
          <ul className="mt-1 list-disc pl-5 text-xs text-tres-text-secondary">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
